import { Button, Sheet, Text, XStack, YStack } from "tamagui";
import { Pressable, TouchableOpacity } from "react-native";
import { CheckIcon } from "react-native-heroicons/solid";
import { Highlight } from "../../../components/design-system/Highlight/Highlight";
import { useState } from "react";

type FilterSheetOption = {
  label: string;
  value: string;
  detail: string;
  icon: React.ReactNode;
};

export const FilterSheet = ({
  isSheetOpen,
  setIsSheetOpen,
  handleApply,
  title,
  options,
}: {
  isSheetOpen: boolean;
  setIsSheetOpen: (open: boolean) => void;
  handleApply: (selectedOptions: string[]) => void;
  title: string;
  options: FilterSheetOption[];
}) => {
  const [tempFilter, setTempFilter] = useState<string[]>(
    options.map((o) => o.value)
  );

  const isApplyDisabled = tempFilter.length === 0;

  const handleSelectAll = () => {
    if (tempFilter.length === options.length) {
      setTempFilter([]);
    } else {
      setTempFilter(options.map((o) => o.value));
    }
  };

  const allSelected = tempFilter.length === options.length;

  return (
    <Sheet
      modal
      open={isSheetOpen}
      onOpenChange={setIsSheetOpen}
      snapPointsMode="fit"
    >
      <Sheet.Overlay />
      <Sheet.Frame bg="$brandParchment" p="$4">
        <XStack
          justifyContent="space-between"
          alignItems="center"
          w="100%"
          borderBottomWidth={1}
          borderBottomColor="black"
          py="$4"
        >
          <Text fontSize={16} fontFamily="$heading" textTransform="uppercase">
            {title}
          </Text>
          <TouchableOpacity
            onPress={handleSelectAll}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Text fontSize={16} textTransform="uppercase">
              Select All
            </Text>
            {allSelected ? (
              <CheckIcon fill="black" color="black" width={16} height={16} />
            ) : null}
          </TouchableOpacity>
        </XStack>
        <YStack mt="$4" gap="$2">
          {options.map((p) => {
            const isSelected = tempFilter.includes(p.value);

            return (
              <Pressable
                onPress={() => {
                  setTempFilter(
                    isSelected
                      ? tempFilter.filter((t) => t !== p.value)
                      : [...tempFilter, p.value]
                  );
                }}
                key={`filter-${p.value}`}
              >
                <XStack gap="$3" alignItems="center">
                  <Highlight width={60} height={40}>
                    {p.icon}
                  </Highlight>
                  <YStack flex={1}>
                    <Text
                      fontSize={18}
                      textTransform="uppercase"
                      color="black"
                      fontFamily="$heading"
                    >
                      {p.label}
                    </Text>
                    <Text
                      fontSize={18}
                      color="black"
                      fontFamily="$interBodyFontItalic"
                    >
                      {p.detail}
                    </Text>
                  </YStack>

                  {isSelected ? (
                    <CheckIcon
                      fill="black"
                      color="black"
                      width={16}
                      height={16}
                    />
                  ) : null}
                </XStack>
              </Pressable>
            );
          })}
        </YStack>
        <Button
          bg="$brandYellowMuted"
          onPress={() => handleApply(tempFilter)}
          mt="$4"
          fontFamily="$heading"
          fontSize={16}
          disabled={isApplyDisabled}
          opacity={isApplyDisabled ? 0.5 : 1}
        >
          APPLY
        </Button>
      </Sheet.Frame>
    </Sheet>
  );
};
